/**
 * Hook para gerenciamento de contrações
 */

import { useState, useCallback, useMemo } from 'react';
import { OrderObject, CONTRACTION_VARIABLE, contractionOptions } from '../types/objects';
import {
  atualizarMultiplicadorUnicoPorStaticID,
  updateObjectMetadataBatch,
  extrairNomeBaseObjeto,
  temContracoesVariaveis
} from '../../../lib/corelOperations';
import { debugLog } from '../../../lib/terminalLogger';

interface UseContractionLogicProps {
  selectedObjects: OrderObject[];
  setSelectedObjects: (objects: OrderObject[]) => void;
  initialContraction?: string; // ✅ Contração inicial baseada na categoria
}

export const useContractionLogic = ({ 
  selectedObjects, 
  setSelectedObjects,
  initialContraction = '0px'
}: UseContractionLogicProps) => {
  const [contraction, setContraction] = useState(initialContraction);

  // Verificar se os objetos têm contrações variáveis
  const hasMultipleContractions = useMemo(() => 
    temContracoesVariaveis(selectedObjects), 
    [selectedObjects]
  );

  // Determinar qual valor deve ser exibido no seletor de contração global
  const globalContractionValue = useMemo(() => {
    return hasMultipleContractions ? CONTRACTION_VARIABLE : (selectedObjects[0]?.contraction || '0px');
  }, [hasMultipleContractions, selectedObjects]);

  // ✅ NOVO: Atualizar contração de todos os objetos usando batch update (otimizado)
  const updateAllContractions = useCallback(async (newContraction: string) => {
    // Validar usando contractionOptions + valores especiais (CONTRACTION_VARIABLE e AUTO)
    const validValues = [...contractionOptions, CONTRACTION_VARIABLE, 'AUTO'];
    if (!validValues.includes(newContraction)) {
      debugLog('ACTIONS:END', { action: 'update-contraction', status: 'error', errorMessage: `Valor de contração inválido: ${newContraction}` });
      return;
    }

    debugLog('ACTIONS:START', { action: 'atualizar-contracao-global-batch', source: 'WEBVIEW', objectCount: selectedObjects.length });
    
    // Preparar array de updates para batch
    const updates = selectedObjects
      .filter(obj => obj.staticId && obj.guid) // Filtrar objetos válidos
      .map(obj => ({
        staticId: obj.staticId,
        guid: obj.guid!,
        multiplicador: obj.quantity, // Preservar quantidade atual
        contracao: newContraction,
        baseName: extrairNomeBaseObjeto(obj.nome)
      }));

    if (updates.length === 0) {
      debugLog('ACTIONS:END', { action: 'update-contraction', status: 'error', errorMessage: 'Nenhum objeto válido para atualizar' });
      return;
    }

    try {
      // ✅ BATCH UPDATE: Enviar todos os objetos de uma vez
      const result = await updateObjectMetadataBatch(updates, (batchResult) => {
        if (batchResult.sucesso && batchResult.updated > 0) {
          // Criar mapa de staticId -> contracao confirmada para atualização eficiente
          const contractionMap = new Map<string, string>();
          
          // Processar itens atualizados com sucesso
          batchResult.items.forEach(item => {
            if (item.staticId) {
              const confirmedContraction = item.contracao != null && item.contracao !== '0' 
                ? String(item.contracao) 
                : '0px';
              contractionMap.set(item.staticId, confirmedContraction);
            }
          });

          // Atualizar estado de uma vez
          setSelectedObjects(prev => {
            const updated = prev.map(obj => {
              const staticId = obj.staticId;
              if (contractionMap.has(staticId)) {
                return {
                  ...obj,
                  contraction: contractionMap.get(staticId)!
                };
              }
              return obj;
            });

            // Verificar se todos têm a mesma contração para atualizar valor global
            const firstContraction = updated[0]?.contraction || '0px';
            const allSame = updated.length > 0 && updated.every(o => (o.contraction || '0px') === firstContraction);
            if (allSame) {
              setContraction(firstContraction);
            }

            return updated;
          });
        }

        // Feedback de erros
        if (batchResult.errors.length > 0) {
          const errorMessages = batchResult.errors.map(e => 
            typeof e === 'string' ? e : e.message || 'Erro desconhecido'
          ).join('\n');
          
          try {
            alert(`Falha ao aplicar contração em ${batchResult.errors.length} de ${updates.length} objetos:\n\n${errorMessages}`);
          } catch {}
        } else if (batchResult.updated < updates.length) {
          try {
            alert(`Apenas ${batchResult.updated} de ${updates.length} objetos foram atualizados.`);
          } catch {}
        }
      });

      debugLog('ACTIONS:END', { 
        action: 'atualizar-contracao-global-batch', 
        status: result.sucesso ? 'success' : 'error',
        updated: result.updated,
        errors: result.errors.length,
        total: updates.length
      });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      debugLog('ACTIONS:END', { 
        action: 'atualizar-contracao-global-batch', 
        status: 'error', 
        errorMessage: errorMsg 
      });
      
      try {
        alert(`Erro ao atualizar contrações: ${errorMsg}`);
      } catch {}
    }
  }, [selectedObjects, setSelectedObjects]);

  // Atualizar a contração de um objeto específico (renderizar somente após confirmação do C#)
  const handleObjectContractionChange = useCallback(async (id: string, newContraction: string) => {
    // Validar usando contractionOptions + valores especiais (CONTRACTION_VARIABLE e AUTO)
    const validValues = [...contractionOptions, CONTRACTION_VARIABLE, 'AUTO'];
    if (!validValues.includes(newContraction)) {
      debugLog('ACTIONS:END', { action: 'update-object-contraction', status: 'error', errorMessage: `Valor de contração inválido: ${newContraction}` });
      return;
    }

    // Buscar o objeto fora do setState
    const objectToUpdate = selectedObjects.find(obj => obj.id === id);
    if (!objectToUpdate) return;

    const usaContracaoVariavel = true;
    const nomeBase = extrairNomeBaseObjeto(objectToUpdate.nome);
    const currentQuantity = objectToUpdate.quantity;

    const handleMultiplicadorAtualizado = (_objetosAtualizados: any[], meta?: { status?: string; itens?: any[] }) => {
      const statusOk = meta?.status === 'ok';
      const itens = Array.isArray(meta?.itens) ? meta!.itens as any[] : undefined;
      const item = itens?.find((x: any) => String(x?.staticId ?? x?.StaticID) === String(objectToUpdate.staticId));
      const sucesso = item ? item.sucesso !== false : statusOk;

      if (!sucesso) {
        try { alert('Falha ao atualizar a contração deste objeto.'); } catch {}
        return;
      }
      const contracaoConfirmada = item?.contracao != null ? String(item.contracao) : newContraction;
      setSelectedObjects(prev => {
        const updated = prev.map(obj => obj.id === id ? {
          ...obj,
          contraction: contracaoConfirmada != null && contracaoConfirmada !== '0' ? String(contracaoConfirmada) : '0px'
        } : obj);
        // Atualizar valor global quando todos coincidirem
        const allSame = updated.every(obj => (obj.contraction || '0px') === (contracaoConfirmada || '0px'));
        if (allSame) setContraction(contracaoConfirmada || '0px');
        return updated;
      });
    };

    await atualizarMultiplicadorUnicoPorStaticID(
      objectToUpdate.staticId, 
      currentQuantity,
      nomeBase,
      handleMultiplicadorAtualizado,
      usaContracaoVariavel,
      newContraction,
      (objectToUpdate as any).guid
    );
  }, [selectedObjects, setSelectedObjects]);

  return {
    contraction,
    setContraction,
    hasMultipleContractions,
    globalContractionValue,
    updateAllContractions,
    handleObjectContractionChange
  };
}; 